import { css } from '@emotion/react';
import { i18next } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { ErrorBadgeIcon, Text } from '@frontend/design-system';

export type SignInErrorTypeKey = keyof typeof errorTypes;
const errorTypes = {
  500: {
    getMessage: () =>
      i18next.t(
        'Our team has been notified, and we are working to resolve the issue as quickly as possible. Please try again later. If the problem persists, please contact Customer Support at (888) 579-5668 for further assistance.',
        { ns: 'SignIn' }
      ),
  },
  401: {
    getMessage: () => i18next.t('Invalid login credentials.', { ns: 'SignIn' }),
  },
  402: {
    getMessage: () =>
      i18next.t(
        'If your email matches a registered account, you will have received instructions on how to regain access. You can also reset your password.',
        { ns: 'SignIn' }
      ),
  },
  ipRestricted: {
    getMessage: () =>
      i18next.t(
        'Your IP address is restricted from accessing this resource. Please contact your Weave administrator if you think this is an error.',
        { ns: 'SignIn' }
      ),
  },
  pawnedPassword: {
    getMessage: () =>
      i18next.t(
        "We've detected that this password matches one found in a public database of exposed passwords. To protect your account, please create a new password.",
        { ns: 'SignIn' }
      ),
  },
  invalidMFACode: {
    getMessage: () =>
      i18next.t('The verification code you entered is incorrect. Please check your code and try again.', {
        ns: 'SignIn',
      }),
  },
  codeSendFailure: {
    getMessage: () => i18next.t('We were unable to send the verification code. Please try again.', { ns: 'SignIn' }),
  },
  webauthnError: {
    getMessage: () => i18next.t('There was an error with your passkey. Please try again.', { ns: 'SignIn' }),
  },
  webauthnSetupError: {
    getMessage: () => i18next.t('We could not set up your passkey.', { ns: 'SignIn' }),
  },
  ssoTenantNotFound: {
    getMessage: () => i18next.t('We could not find a tenant with that identifier.', { ns: 'SignIn' }),
  },
  oauth2InitError: {
    getMessage: () =>
      i18next.t('There was an error initializing the login process. Please refresh the page and try again.', {
        ns: 'SignIn',
      }),
  },
  consentFlowError: {
    getMessage: () =>
      i18next.t('There was an error processing your consent. Please refresh the page and try again.', { ns: 'SignIn' }),
  },
  unknown: {
    getMessage: () => i18next.t('An unknown error occurred. Please refresh the page and try again.', { ns: 'SignIn' }),
  },
  // Add more error codes in future
};

interface SignInErrorProps {
  errorCode: SignInErrorTypeKey | undefined;
}

export const SignInError = ({ errorCode }: SignInErrorProps) => {
  const errorType = errorCode ? errorTypes[errorCode] ?? errorTypes.unknown : errorTypes.unknown;
  const errorMessage = errorType.getMessage();

  return (
    <div
      role='alert'
      css={css`
        border: 1px solid ${theme.colors.critical50};
        background: ${theme.colors.critical5};
        border-radius: ${theme.borderRadius.medium};
        margin: ${theme.spacing(2)} 0;
        padding: ${theme.spacing(2)};
        display: flex;
      `}
    >
      <Text
        size='medium'
        css={css`
          display: flex;
          gap: ${theme.spacing(2)};
          align-items: center;
        `}
      >
        <ErrorBadgeIcon
          name='error-badge'
          color='error'
          css={css`
            cursor: pointer;
          `}
        />
        {errorMessage}
      </Text>
    </div>
  );
};
